import { WizardTrigger } from "apis/procore";

import { type LifecycleWriter, sharedDialogLifecycle } from "./dialogLifecycle";
import { resetGpuProbeCache } from "./gpuProbeCache";
import { type GuidanceSnapshot } from "./guidanceModel";
import { resetGpuDetectionTelemetry, trackClassified } from "./telemetry";

type Listener = () => void;

let snapshot: GuidanceSnapshot | null = null;
let lifecycleWriter: LifecycleWriter = sharedDialogLifecycle;
let rerun: (() => Promise<void>) | undefined;
let wizardOpenToken = 0;
let lastWizardTrigger: WizardTrigger = WizardTrigger.Auto;
const listeners = new Set<Listener>();

export function getGuidanceSnapshot(): GuidanceSnapshot | null {
    return snapshot;
}

export function subscribeGuidanceSnapshot(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function setGuidanceSnapshot(nextSnapshot: GuidanceSnapshot) {
    snapshot = nextSnapshot;
    trackClassified(nextSnapshot);
    notify();
}

export function setGuidanceLifecycleWriter(nextLifecycleWriter: LifecycleWriter) {
    lifecycleWriter = nextLifecycleWriter;
}

export function setGuidanceRerun(nextRerun: () => Promise<void>) {
    rerun = nextRerun;
}

export async function ignoreGuidanceDialog() {
    lifecycleWriter.ignore();
    await rerun?.();
}

export async function confirmGuidanceAdjusted() {
    lifecycleWriter.confirmAdjusted();
    await rerun?.();
}

/**
 * Marks the one-time ghost nudge as shown. The UI must call this when it actually
 * displays the nudge; detection itself never consumes it (it only publishes
 * `ghostNudgePending` on the snapshot).
 */
export async function consumeGuidanceGhostNudge() {
    lifecycleWriter.consumeGhostNudge();
    await rerun?.();
}

export async function resetGuidanceLifecycle() {
    lifecycleWriter.reset();
    await rerun?.();
}

/**
 * Forces the guidance parent to open the step-by-step dialog, skipping the intro.
 * Used by later tickets (hard-stop Get Started, banner CTA). Additive: each call
 * increments a token so the parent can distinguish a new request from a stale one.
 *
 * The `trigger` is stored so `GuidanceDialog` can read it when it fires
 * `gpu_wizard_shown`. Callers must pass an explicit arrow function rather than
 * this directly — a direct ref would receive a MouseEvent from Core React's Button.
 */
export function openGuidanceWizard(trigger: WizardTrigger): void {
    lastWizardTrigger = trigger;
    wizardOpenToken += 1;
    notify();
}

export function getGuidanceWizardOpenToken(): number {
    return wizardOpenToken;
}

export function getGuidanceWizardTrigger(): WizardTrigger {
    return lastWizardTrigger;
}

/** Clears in-memory snapshot, wizard token, telemetry dedupe state, and detection probe cache. Used by tests. */
export function resetGuidanceStore() {
    snapshot = null;
    wizardOpenToken = 0;
    lastWizardTrigger = WizardTrigger.Auto;
    resetGpuDetectionTelemetry();
    resetGpuProbeCache();
    notify();
}

function notify() {
    for (const listener of listeners) {
        listener();
    }
}
